import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Trash2, Calendar } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatCurrency } from '@/lib/utils';

const poLineSchema = z.object({
  product_id: z.number(),
  product_name: z.string(),
  sku: z.string(),
  quantity: z.number().min(1),
  unit_price: z.number().min(0),
  total: z.number(),
});

const purchaseOrderSchema = z.object({
  po_number: z.string().min(1, 'PO Number is required'),
  supplier_id: z.number({ required_error: 'Please select a supplier' }),
  expected_delivery: z.string().min(1, 'Expected delivery date is required'),
  notes: z.string().optional(),
  lines: z.array(poLineSchema).min(1, 'At least one line item is required'),
});

type POFormData = z.infer<typeof purchaseOrderSchema>;
type POLine = z.infer<typeof poLineSchema>;

interface Supplier {
  id: number;
  name: string;
  payment_terms: string;
}

interface Product {
  id: number;
  sku: string;
  name: string;
  manufacturer?: string;
  unit_price: number;
}

interface PurchaseOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suppliers: Supplier[];
  products: Product[];
  existingPO?: {
    id: number;
    po_number: string;
    supplier_id: number;
    expected_delivery: string;
    notes?: string;
    lines: POLine[];
  };
  onSubmit: (data: POFormData) => void;
  isLoading?: boolean;
}

export function PurchaseOrderDialog({
  open,
  onOpenChange,
  suppliers,
  products,
  existingPO,
  onSubmit,
  isLoading,
}: PurchaseOrderDialogProps) {
  const [lines, setLines] = useState<POLine[]>(existingPO?.lines || []);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [isCustomProduct, setIsCustomProduct] = useState(false);
  const [customProductName, setCustomProductName] = useState('');
  const [customProductSku, setCustomProductSku] = useState('');
  const [customProductPrice, setCustomProductPrice] = useState(0);

  // Filter products by selected supplier (manufacturer)
  const getFilteredProducts = (supplierId: number | undefined) => {
    if (!supplierId) return [];
    const supplier = suppliers.find(s => s.id === supplierId);
    if (!supplier) return [];
    return products.filter(p => p.manufacturer === supplier.name);
  };

  const form = useForm<POFormData>({
    resolver: zodResolver(purchaseOrderSchema),
    defaultValues: {
      po_number: existingPO?.po_number || '',
      supplier_id: existingPO?.supplier_id,
      expected_delivery: existingPO?.expected_delivery || '',
      notes: existingPO?.notes || '',
      lines: existingPO?.lines || [],
    },
  });

  useEffect(() => {
    if (existingPO) {
      form.reset({
        po_number: existingPO.po_number,
        supplier_id: existingPO.supplier_id,
        expected_delivery: existingPO.expected_delivery,
        notes: existingPO.notes || '',
        lines: existingPO.lines,
      });
      setLines(existingPO.lines);
    }
  }, [existingPO, form]);

  // Clear lines when supplier changes (for new POs only)
  const currentSupplierId = form.watch('supplier_id');
  useEffect(() => {
    if (!existingPO && currentSupplierId) {
      setLines([]);
      setSelectedProduct('');
    }
  }, [currentSupplierId, existingPO]);

  const addLine = () => {
    if (isCustomProduct) {
      // Add custom product
      if (!customProductName.trim()) return;

      // Generate a unique negative ID for custom products
      const customId = -Date.now();
      const newLine: POLine = {
        product_id: customId,
        product_name: customProductName.trim(),
        sku: customProductSku.trim() || 'CUSTOM',
        quantity: 1,
        unit_price: customProductPrice,
        total: customProductPrice,
      };
      setLines([...lines, newLine]);

      // Reset custom product fields
      setCustomProductName('');
      setCustomProductSku('');
      setCustomProductPrice(0);
      return;
    }

    if (!selectedProduct) return;

    const filteredProducts = getFilteredProducts(form.watch('supplier_id'));
    const product = filteredProducts.find(p => p.id === parseInt(selectedProduct));
    if (!product) return;

    const existingLine = lines.find(l => l.product_id === product.id);
    if (existingLine) {
      // Update quantity if product already exists
      setLines(lines.map(l =>
        l.product_id === product.id
          ? { ...l, quantity: l.quantity + 1, total: (l.quantity + 1) * l.unit_price }
          : l
      ));
    } else {
      // Add new line
      const newLine: POLine = {
        product_id: product.id,
        product_name: product.name,
        sku: product.sku,
        quantity: 1,
        unit_price: product.unit_price,
        total: product.unit_price,
      };
      setLines([...lines, newLine]);
    }
    setSelectedProduct('');
  };

  const updateLineQuantity = (productId: number, quantity: number) => {
    if (quantity < 1) return;
    setLines(lines.map(l =>
      l.product_id === productId
        ? { ...l, quantity, total: quantity * l.unit_price }
        : l
    ));
  };

  const updateLinePrice = (productId: number, unitPrice: number) => {
    if (unitPrice < 0) return;
    setLines(lines.map(l =>
      l.product_id === productId
        ? { ...l, unit_price: unitPrice, total: l.quantity * unitPrice }
        : l
    ));
  };

  const removeLine = (productId: number) => {
    setLines(lines.filter(l => l.product_id !== productId));
  };

  // Sync lines state with form
  useEffect(() => {
    form.setValue('lines', lines);
  }, [lines, form]);

  const totalValue = lines.reduce((sum, line) => sum + line.total, 0);

  const handleSubmit = (data: Omit<POFormData, 'lines'>) => {
    if (lines.length === 0) {
      form.setError('lines', { message: 'At least one line item is required' });
      return;
    }
    onSubmit({ ...data, lines });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {existingPO ? 'Edit Purchase Order' : 'Create Purchase Order'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          {/* Supplier Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Supplier *</Label>
              <Select
                value={form.watch('supplier_id')?.toString()}
                onValueChange={(value) => form.setValue('supplier_id', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id.toString()}>
                      {supplier.name}
                      <span className="text-xs text-muted-foreground ml-2">
                        ({supplier.payment_terms})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.supplier_id && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.supplier_id.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Expected Delivery *</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="date"
                  {...form.register('expected_delivery')}
                  className="pl-10"
                />
              </div>
              {form.formState.errors.expected_delivery && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.expected_delivery.message}
                </p>
              )}
            </div>
          </div>

          {/* Line Items Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Line Items</Label>
              <p className="text-sm text-muted-foreground">
                {lines.length} item(s)
              </p>
            </div>

            {/* Add Product */}
            <div className="space-y-3">
              {/* Toggle between dropdown and custom */}
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setIsCustomProduct(false)}
                  className={`text-sm font-medium ${!isCustomProduct ? 'text-primary underline' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Select from list
                </button>
                <span className="text-muted-foreground">|</span>
                <button
                  type="button"
                  onClick={() => setIsCustomProduct(true)}
                  className={`text-sm font-medium ${isCustomProduct ? 'text-primary underline' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Add custom product
                </button>
              </div>

              {isCustomProduct ? (
                /* Custom Product Entry */
                <div className="flex gap-2 items-end">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Product Name *</Label>
                    <Input
                      value={customProductName}
                      onChange={(e) => setCustomProductName(e.target.value)}
                      placeholder="Enter product name"
                    />
                  </div>
                  <div className="w-28 space-y-1">
                    <Label className="text-xs">SKU</Label>
                    <Input
                      value={customProductSku}
                      onChange={(e) => setCustomProductSku(e.target.value)}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="w-28 space-y-1">
                    <Label className="text-xs">Price</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={customProductPrice}
                      onChange={(e) => setCustomProductPrice(parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                    />
                  </div>
                  <Button type="button" onClick={addLine} disabled={!customProductName.trim()}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>
              ) : (
                /* Dropdown Selection */
                <>
                  {form.watch('supplier_id') && getFilteredProducts(form.watch('supplier_id')).length === 0 ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200">
                      No products found for this supplier. Use "Add custom product" to enter items manually.
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Select
                        value={selectedProduct}
                        onValueChange={setSelectedProduct}
                        disabled={!form.watch('supplier_id')}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder={form.watch('supplier_id') ? "Select product to add" : "Select a supplier first"} />
                        </SelectTrigger>
                        <SelectContent>
                          {getFilteredProducts(form.watch('supplier_id')).map((product) => (
                            <SelectItem key={product.id} value={product.id.toString()}>
                              <div className="flex flex-col">
                                <span>{product.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  {product.sku}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button type="button" onClick={addLine} disabled={!selectedProduct}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* PO Number */}
            <div className="space-y-2">
              <Label>PO Number *</Label>
              <Input
                {...form.register('po_number')}
                placeholder="Enter PO number (e.g., PO-2026-0001)"
              />
              {form.formState.errors.po_number && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.po_number.message}
                </p>
              )}
            </div>

            {/* Line Items Table */}
            {lines.length > 0 ? (
              <div className="rounded-lg border">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-3 py-2 text-left text-sm font-medium">Product</th>
                      <th className="px-3 py-2 text-right text-sm font-medium w-24">Qty</th>
                      <th className="px-3 py-2 text-right text-sm font-medium w-32">Unit Price</th>
                      <th className="px-3 py-2 text-right text-sm font-medium w-28">Total</th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line) => (
                      <tr key={line.product_id} className="border-b last:border-0">
                        <td className="px-3 py-2">
                          <p className="font-medium text-sm">{line.product_name}</p>
                          <p className="text-xs font-mono text-muted-foreground">
                            {line.sku}
                          </p>
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            min="1"
                            value={line.quantity}
                            onChange={(e) =>
                              updateLineQuantity(line.product_id, parseInt(e.target.value) || 1)
                            }
                            className="w-20 text-right"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.unit_price}
                            onChange={(e) =>
                              updateLinePrice(line.product_id, parseFloat(e.target.value) || 0)
                            }
                            className="w-28 text-right"
                          />
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {formatCurrency(line.total)}
                        </td>
                        <td className="px-3 py-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeLine(line.product_id)}
                            className="h-8 w-8 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/30">
                      <td colSpan={3} className="px-3 py-2 text-right font-semibold">
                        Total:
                      </td>
                      <td className="px-3 py-2 text-right font-bold text-lg">
                        {formatCurrency(totalValue)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
                <p>No line items added yet</p>
                <p className="text-sm">Select a product above to add items</p>
              </div>
            )}

            {form.formState.errors.lines && (
              <p className="text-sm text-destructive">
                {form.formState.errors.lines.message}
              </p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              {...form.register('notes')}
              placeholder="Additional notes or instructions for the supplier..."
              rows={3}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || lines.length === 0}>
              {isLoading ? 'Saving...' : existingPO ? 'Update PO' : 'Create PO'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
